import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export default function Messages() {
  const [reservations, setReservations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedRes, setSelectedRes] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reservations', { params: { limit: 50 } }).then(r => setReservations(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedRes) {
      api.get('/messages', { params: { reservation_id: selectedRes } }).then(r => setMessages(r.data)).catch(console.error);
    }
  }, [selectedRes]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedRes) return;
    setSendLoading(true);
    const res = reservations.find(r => r.id === selectedRes);
    try {
      await api.post('/messages', { reservation_id: selectedRes, guest_id: res?.guest_id || '', sender_type: 'staff', message: newMessage, sent_via: 'app' });
      setNewMessage('');
      const msgs = await api.get('/messages', { params: { reservation_id: selectedRes } });
      setMessages(msgs.data);
    } catch (e) { console.error(e); }
    finally { setSendLoading(false); }
  };

  const handleAISuggest = async () => {
    if (!selectedRes) return;
    setSuggestLoading(true);
    const res = reservations.find(r => r.id === selectedRes);
    try {
      const { data } = await api.post('/messages/ai-suggest', { reservation_id: selectedRes, guest_name: res?.guest_name || 'Guest', context: `Unit: ${res?.unit_name}` });
      setAiSuggestion(data.suggestion);
    } catch (e) { console.error(e); setAiSuggestion('Failed to get suggestion.'); }
    finally { setSuggestLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="messages-page">
      <div className="page-header">
        <h1 className="page-title">Messages</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Reservation list */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900">Reservations</h3>
          </div>
          <div className="max-h-[300px] lg:max-h-[600px] overflow-y-auto">
            {reservations.map(r => (
              <button
                key={r.id}
                onClick={() => { setSelectedRes(r.id); setAiSuggestion(''); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${selectedRes === r.id ? 'bg-slate-100' : ''}`}
                data-testid={`msg-res-${r.id}`}
              >
                <p className="text-sm font-medium text-slate-900 truncate">{r.guest_name || 'Guest'}</p>
                <p className="text-xs text-slate-500">{r.unit_name} &middot; {r.check_in ? format(new Date(r.check_in), 'MMM dd') : ''}</p>
              </button>
            ))}
            {reservations.length === 0 && <p className="p-4 text-sm text-slate-400">No reservations</p>}
          </div>
        </div>

        {/* Messages */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg flex flex-col min-h-[350px] sm:min-h-[500px]">
          {selectedRes ? (
            <>
              <div className="p-4 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900">Conversation</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] px-4 py-2 rounded-lg text-sm ${m.sender_type === 'staff' ? 'bg-slate-900 text-white' : m.sender_type === 'ai' ? 'bg-blue-50 text-blue-900 border border-blue-200' : 'bg-slate-100 text-slate-900'}`}>
                      <p>{m.message}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_type === 'staff' ? 'text-slate-400' : 'text-slate-500'}`}>{m.created_at ? format(new Date(m.created_at), 'HH:mm') : ''} &middot; {m.sender_type}{m.ai_suggested ? ' (AI)' : ''}</p>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && <p className="text-center text-sm text-slate-400 py-12">No messages yet</p>}
              </div>

              {aiSuggestion && (
                <div className="mx-4 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-700 mb-1">AI Suggestion</p>
                  <p className="text-sm text-blue-900">{aiSuggestion}</p>
                  <Button size="sm" variant="ghost" className="mt-2 text-blue-700" onClick={() => { setNewMessage(aiSuggestion); setAiSuggestion(''); }} data-testid="use-ai-suggestion-btn">Use this</Button>
                </div>
              )}

              <div className="p-4 border-t border-slate-200 flex gap-2">
                <Button variant="outline" size="sm" onClick={handleAISuggest} disabled={suggestLoading} data-testid="ai-suggest-btn">
                  {suggestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                </Button>
                <Input
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  data-testid="message-input"
                />
                <Button onClick={handleSend} disabled={sendLoading || !newMessage.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="send-message-btn">
                  {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a reservation to view messages</div>
          )}
        </div>
      </div>
    </div>
  );
}
